import { useState, useEffect, useRef } from 'react'
import { Fishbrain } from '../api'
import AppHeader from '../components/AppHeader'

// Public Cognito identifiers (see specs/fishbrain-export-tool/fishbrain-api-notes.md).
// Not secrets — visible to any logged-in fishbrain.com browser session.
const FISHBRAIN_COGNITO_REGION = 'eu-west-1'
const FISHBRAIN_COGNITO_CLIENT_ID = '6a0ka5u9updjin5so6ot5jblf0'
const FISHBRAIN_COGNITO_URL = `https://cognito-idp.${FISHBRAIN_COGNITO_REGION}.amazonaws.com/`

interface Props {
  onNavigate: (target: string) => void
  onLogout: () => void
}

type ExportStatus = 'idle' | 'running' | 'complete' | 'error'

async function cognitoPasswordAuth(email: string, password: string): Promise<string> {
  const resp = await fetch(FISHBRAIN_COGNITO_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
    },
    body: JSON.stringify({
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: FISHBRAIN_COGNITO_CLIENT_ID,
      AuthParameters: { USERNAME: email, PASSWORD: password },
    }),
  })
  const data = await resp.json()
  if (data.ChallengeName) {
    throw new Error('MFA not supported')
  }
  if (!resp.ok || !data.AuthenticationResult) {
    throw new Error(data.message ?? 'Fishbrain sign-in failed')
  }
  return data.AuthenticationResult.RefreshToken as string
}

export default function FishbrainExportPage({ onNavigate, onLogout }: Props) {
  const [connected, setConnected] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loginError, setLoginError] = useState<string | null>(null)
  const [loginLoading, setLoginLoading] = useState(false)

  const [status, setStatus] = useState<ExportStatus>('idle')
  const [done, setDone] = useState(0)
  const [total, setTotal] = useState<number | null>(null)
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null)
  const [exportError, setExportError] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [])

  async function connectFishbrain() {
    setLoginError(null)
    setLoginLoading(true)
    try {
      const refreshToken = await cognitoPasswordAuth(email, password)
      await Fishbrain.fishbrainLogin({ fishbrainLoginRequest: { refreshToken } })
      setConnected(true)
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : 'Fishbrain sign-in failed')
    } finally {
      setLoginLoading(false)
    }
  }

  function handleLoginSubmit(e: React.FormEvent) {
    e.preventDefault()
    connectFishbrain()
  }

  function pollStatus(flowRunId: string) {
    pollRef.current = setInterval(async () => {
      try {
        const r = await Fishbrain.fishbrainExportStatus({
          fishbrainExportStatusRequest: { flowRunId },
        })
        setDone(r.done)
        setTotal(r.total)
        if (r.status === 'complete') {
          setStatus('complete')
          setDownloadUrl(r.downloadUrl)
          if (pollRef.current) clearInterval(pollRef.current)
        } else if (r.status === 'error') {
          setStatus('error')
          setExportError(r.error ?? 'Export failed')
          if (pollRef.current) clearInterval(pollRef.current)
        }
      } catch {
        // Transient poll failure — keep trying on the next tick.
      }
    }, 2500)
  }

  async function handleStartExport() {
    setStatus('running')
    setExportError(null)
    setDownloadUrl(null)
    setDone(0)
    setTotal(null)
    try {
      const r = await Fishbrain.fishbrainExportStart()
      pollStatus(r.flowRunId)
    } catch {
      setStatus('error')
      setExportError('Could not start export')
    }
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <AppHeader active="fishbrain-export" onNavigate={onNavigate} onLogout={onLogout} />

      <div className="max-w-lg mx-auto px-4 py-8 space-y-6">
        <div>
          <h2 className="font-display font-black text-xl text-stone-900 mb-1">Fishbrain export</h2>
          <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-400">
            Internal tool — export your own Fishbrain catches
          </p>
        </div>

        {!connected && (
          <div className="bg-white rounded-2xl border border-stone-200 p-5">
            <h3 className="font-mono text-xs tracking-[0.15em] uppercase text-stone-500 mb-4">
              Connect Fishbrain
            </h3>
            <form onSubmit={handleLoginSubmit} className="space-y-3">
              <div>
                <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-stone-500 mb-1.5">
                  Fishbrain email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2.5
                    font-mono text-sm text-stone-900 placeholder-stone-400
                    focus:outline-none focus:border-bear/60 transition-colors"
                  placeholder="you@example.com"
                  autoComplete="username"
                  required
                />
              </div>
              <div>
                <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-stone-500 mb-1.5">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2.5
                    font-mono text-sm text-stone-900 placeholder-stone-400
                    focus:outline-none focus:border-bear/60 transition-colors"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                />
              </div>
              {loginError && (
                <p className="font-mono text-[10px] text-coral">{loginError}</p>
              )}
              <button
                type="submit"
                disabled={loginLoading}
                className="w-full px-4 py-2.5 rounded-xl bg-bear text-white
                  font-mono text-[11px] tracking-[0.15em] uppercase font-semibold
                  hover:bg-bear-light transition-colors cursor-pointer
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loginLoading ? 'Connecting…' : 'Connect'}
              </button>
            </form>
          </div>
        )}

        {connected && (
          <div className="bg-white rounded-2xl border border-stone-200 p-5 space-y-4">
            <h3 className="font-mono text-xs tracking-[0.15em] uppercase text-stone-500">Export</h3>

            {status === 'idle' && (
              <button
                onClick={handleStartExport}
                className="w-full px-4 py-2.5 rounded-xl bg-bear text-white
                  font-mono text-[11px] tracking-[0.15em] uppercase font-semibold
                  hover:bg-bear-light transition-colors cursor-pointer"
              >
                Export my catches
              </button>
            )}

            {status === 'running' && (
              <div>
                <p className="font-mono text-xs text-stone-700">
                  Exporting… {done}{total != null ? ` / ${total}` : ''}
                </p>
                <div className="w-full h-1.5 bg-stone-100 rounded-full mt-2 overflow-hidden">
                  <div
                    className="h-full bg-bear transition-all"
                    style={{ width: total ? `${Math.min(100, (done / total) * 100)}%` : '20%' }}
                  />
                </div>
              </div>
            )}

            {status === 'complete' && downloadUrl && (
              <button
                onClick={() => { window.location.href = downloadUrl }}
                className="w-full px-4 py-2.5 rounded-xl bg-bear text-white
                  font-mono text-[11px] tracking-[0.15em] uppercase font-semibold
                  hover:bg-bear-light transition-colors cursor-pointer"
              >
                Download export
              </button>
            )}

            {status === 'error' && (
              <p className="font-mono text-[10px] text-coral">{exportError}</p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
